import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { subscriptionAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  CheckCircle2, 
  Sparkles, 
  Star, 
  Zap, 
  Shield, 
  Clock, 
  ArrowRight,
  Loader2,
  Gift,
  Users,
  TrendingUp,
  Calendar,
  BarChart3,
  Package
} from 'lucide-react';

export const Pricing = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    fetchData();
    
    // Check if coming back from cancelled checkout
    if (searchParams.get('cancelled') === 'true') {
      toast.info('Checkout cancelled. You can try again anytime.');
    }
  }, [searchParams, user]);

  const fetchData = async () => {
    try {
      const pricingRes = await subscriptionAPI.getPricing();
      setPricing(pricingRes.data);
      
      if (user) {
        const statusRes = await subscriptionAPI.getStatus();
        setSubscriptionStatus(statusRes.data);
      }
    } catch (error) {
      console.error('Failed to fetch pricing');
    } finally {
      setLoading(false);
    }
  };

  const [selectedPlan, setSelectedPlan] = useState('monthly');

  const handleSubscribe = async () => {
    if (!user) {
      navigate('/signup');
      return;
    }

    setCheckoutLoading(true);
    try {
      const originUrl = window.location.origin;
      const res = await subscriptionAPI.createCheckout(originUrl, selectedPlan);
      
      // Redirect to Stripe Checkout
      if (res.data.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (error) {
      toast.error('Failed to start checkout. Please try again.');
      setCheckoutLoading(false);
    }
  };

  const features = [
    { icon: Sparkles, text: 'AI Event Scout - Find perfect shows for your products' },
    { icon: TrendingUp, text: 'True profit tracking across all channels' },
    { icon: Package, text: 'Etsy integration with automatic fee calculation' },
    { icon: Calendar, text: 'Event calendar with deadline reminders' },
    { icon: BarChart3, text: 'Analytics dashboard with channel breakdowns' },
    { icon: Shield, text: 'Boutique consignment tracking' },
    { icon: Zap, text: 'What-If Planner for season modeling' },
    { icon: Users, text: 'Unlimited products and events' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFFFFF] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#1E3A5F]" />
      </div>
    );
  }

  const isEarlyBird = pricing?.early_bird_available;
  const monthlyPrice = pricing?.monthly_price || 9.99;
  const annualPrice = pricing?.annual_price || 95.90;
  const annualDiscount = pricing?.annual_discount_percent || 20;
  const trialDays = pricing?.trial_days || 7;
  const regularPrice = pricing?.regular_price || 19.99;
  const spotsRemaining = pricing?.spots_remaining || 0;

  // Check if user already has active subscription
  const hasActiveSubscription = subscriptionStatus?.is_active;
  const isInTrial = subscriptionStatus?.status === 'trial' && subscriptionStatus?.can_access;
  const trialExpired = subscriptionStatus?.status === 'expired';

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FFFFFF] to-[#FFEDDF]">
      {/* Hero */}
      <div className="max-w-5xl mx-auto px-4 py-16 text-center">
        {/* Early Bird Banner */}
        {isEarlyBird && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1E3A5F] to-[#C49B1A] rounded-full text-white font-medium mb-6 animate-pulse">
            <Gift className="w-5 h-5" />
            <span>Early Bird Pricing - Only {spotsRemaining} spots left!</span>
          </div>
        )}

        <h1 className="text-4xl md:text-5xl font-bold font-['Outfit'] text-[#1E3A5F] mb-4">
          Invest in Your Craft Business
        </h1>
        <p className="text-lg text-[#4A4A4A] max-w-2xl mx-auto mb-8">
          Stop losing money on wrong shows. Stop guessing your real profit. 
          Get the tools serious artisans use to grow their business.
        </p>

        {/* Trial Status Banner */}
        {user && isInTrial && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 border-2 border-blue-300 rounded-xl text-blue-800 font-medium mb-8">
            <Clock className="w-5 h-5" />
            <span>Your free trial has {subscriptionStatus.days_remaining} days remaining</span>
          </div>
        )}

        {user && trialExpired && (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-100 border-2 border-red-300 rounded-xl text-red-800 font-medium mb-8">
            <Clock className="w-5 h-5" />
            <span>Your free trial has ended. Subscribe to continue using AMSCompass.</span>
          </div>
        )}

        {/* Pricing Card */}
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl border-4 border-[#1E3A5F] shadow-hard overflow-hidden">
            {/* Header */}
            <div className="bg-[#1E3A5F] text-white py-6 px-6">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Star className="w-6 h-6 text-[#1E3A5F]" />
                <h2 className="text-2xl font-bold font-['Outfit']">AMSCompass</h2>
              </div>
              <p className="text-white/80">Everything you need to run your craft business</p>
            </div>

            {/* Price - Plan Selection */}
            <div className="py-8 px-6">
              {/* Monthly Plan */}
              <div 
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all mb-3 ${
                  selectedPlan === 'monthly' 
                    ? 'border-[#1E3A5F] bg-[#1E3A5F]/5 ring-2 ring-[#1E3A5F]/20' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSelectedPlan('monthly')}
                data-testid="plan-monthly"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'monthly' ? 'border-[#1E3A5F]' : 'border-gray-300'
                    }`}>
                      {selectedPlan === 'monthly' && <div className="w-3 h-3 rounded-full bg-[#1E3A5F]" />}
                    </div>
                    <div>
                      <p className="font-bold text-[#1E3A5F]">Monthly</p>
                      <p className="text-xs text-[#71717A]">Billed monthly</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-[#1E3A5F]">${monthlyPrice.toFixed(2)}</span>
                    <span className="text-sm text-[#4A4A4A]">/mo</span>
                  </div>
                </div>
              </div>
              
              {/* Annual Plan */}
              <div 
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedPlan === 'annual' 
                    ? 'border-green-600 bg-green-50 ring-2 ring-green-200' 
                    : 'border-green-200 hover:border-green-300 bg-green-50/50'
                }`}
                onClick={() => setSelectedPlan('annual')}
                data-testid="plan-annual"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      selectedPlan === 'annual' ? 'border-green-600' : 'border-green-300'
                    }`}>
                      {selectedPlan === 'annual' && <div className="w-3 h-3 rounded-full bg-green-600" />}
                    </div>
                    <div>
                      <p className="font-bold text-green-800">Annual</p>
                      <p className="text-xs text-green-600">Save {annualDiscount}%</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-green-700">${annualPrice.toFixed(2)}</span>
                    <span className="text-sm text-green-600">/yr</span>
                  </div>
                </div>
              </div>

              <p className="text-sm text-[#71717A] mt-4">
                {trialDays}-day free trial. Cancel anytime. No long-term commitment.
              </p>
            </div>

            {/* CTA */}
            <div className="px-6 pb-6">
              {hasActiveSubscription ? (
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 px-4 py-3 bg-green-100 text-green-700 rounded-xl font-medium">
                    <CheckCircle2 className="w-5 h-5" />
                    You're subscribed!
                  </div>
                  <Link to="/dashboard">
                    <Button className="w-full mt-4 btn-primary">
                      Go to Dashboard
                    </Button>
                  </Link>
                </div>
              ) : (
                <Button 
                  onClick={handleSubscribe}
                  disabled={checkoutLoading}
                  className="w-full bg-[#1E3A5F] hover:bg-[#1E3A5F] text-white font-bold text-lg py-6"
                  data-testid="subscribe-btn"
                >
                  {checkoutLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  ) : (
                    <Zap className="w-5 h-5 mr-2" />
                  )}
                  {user ? 'Subscribe Now' : 'Start Free Trial'}
                </Button>
              )}
              
              {!user && (
                <p className="text-sm text-center text-[#71717A] mt-4">
                  7-day free trial • No credit card required to start
                </p>
              )}
            </div>

            {/* Features */}
            <div className="border-t-2 border-[#E5E5E5] bg-[#FFFFFF] px-6 py-6">
              <p className="font-bold text-[#1E3A5F] mb-4">Everything included:</p>
              <ul className="space-y-3">
                {features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <feature.icon className="w-5 h-5 text-[#1E3A5F] flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-[#4A4A4A]">{feature.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Guarantee */}
        <div className="mt-12 max-w-2xl mx-auto">
          <div className="bg-white rounded-xl border-2 border-[#1E3A5F] p-6 flex items-start gap-4">
            <Shield className="w-10 h-10 text-[#1E3A5F] flex-shrink-0" />
            <div className="text-left">
              <h3 className="font-bold text-[#1E3A5F] mb-1">30-Day Money-Back Guarantee</h3>
              <p className="text-sm text-[#4A4A4A]">
                Try AMSCompass risk-free. If you don't love it within 30 days, 
                we'll refund your subscription—no questions asked.
              </p>
            </div>
          </div>
        </div>

        {/* FAQ */}
        <div className="mt-16 text-left max-w-2xl mx-auto">
          <h2 className="text-2xl font-bold font-['Outfit'] text-[#1E3A5F] text-center mb-8">
            Frequently Asked Questions
          </h2>
          
          <div className="space-y-4">
            <div className="bg-white rounded-xl border-2 border-[#E5E5E5] p-5">
              <h3 className="font-bold text-[#1E3A5F] mb-2">What happens after my free trial?</h3>
              <p className="text-sm text-[#4A4A4A]">
                After 7 days, you'll need to subscribe to continue accessing AMSCompass. 
                Your data is always saved, so you can pick up right where you left off.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border-2 border-[#E5E5E5] p-5">
              <h3 className="font-bold text-[#1E3A5F] mb-2">What's early bird pricing?</h3>
              <p className="text-sm text-[#4A4A4A]">
                Our first 50 subscribers lock in $9.99/month forever—even after we raise the price to $19.99. 
                It's our way of thanking early supporters.
              </p>
            </div>
            
            <div className="bg-white rounded-xl border-2 border-[#E5E5E5] p-5">
              <h3 className="font-bold text-[#1E3A5F] mb-2">Can I cancel anytime?</h3>
              <p className="text-sm text-[#4A4A4A]">
                Yes! Cancel with one click from your Settings page. No long-term contracts, no hassle.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
